import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Eye, FileText, Clock, CheckCircle, AlertCircle, RefreshCw, Lightbulb, Users, Sparkles, Brain, Briefcase, GraduationCap, Code, FolderKanban } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import CandidateDetailDialog from '@/components/CandidateDetailDialog';
import { formatDistanceToNow } from 'date-fns';

interface ParsedCandidatesViewProps {
  jobId?: string;
  showAsTab?: boolean;
}

interface ExtractionStats {
  experience_count?: number;
  education_count?: number;
  skills_count?: number;
  projects_count?: number;
  text_improved?: boolean;
}

interface ParsedCandidate {
  id: string;
  candidate_id: string;
  resume_id: string;
  job_id: string | null;
  status: string;
  current_phase: string;
  progress_percent: number;
  extraction_stats: ExtractionStats | null;
  ai_suggestions: string[] | null;
  duplicate_candidate_id: string | null;
  created_at: string;
  completed_at: string | null;
  error_message: string | null;
  candidate: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email_primary: string | null;
  };
  resume: {
    original_filename: string;
  };
  job?: {
    id: string;
    title: string;
  };
}

const phaseLabels: Record<string, string> = {
  pending: 'Queued',
  extracting_text: 'Extracting text',
  extracting_personal_info: 'Finding contact info',
  extracting_experience: 'Parsing experience',
  extracting_education: 'Parsing education',
  extracting_skills: 'Identifying skills',
  extracting_projects: 'Finding projects',
  improving_text: 'Improving text',
  linking_skills: 'Linking skills',
  completed: 'Complete',
  failed: 'Failed',
};

const ParsedCandidatesView = ({ jobId, showAsTab = false }: ParsedCandidatesViewProps) => {
  const [parsedCandidates, setParsedCandidates] = useState<ParsedCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [expandedSuggestions, setExpandedSuggestions] = useState<Set<string>>(new Set());

  const fetchParsedCandidates = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('parsing_jobs')
        .select(`
          id,
          candidate_id,
          resume_id,
          job_id,
          status,
          current_phase,
          progress_percent,
          extraction_stats,
          ai_suggestions,
          duplicate_candidate_id,
          created_at,
          completed_at,
          error_message,
          candidate:candidates!parsing_jobs_candidate_id_fkey (
            id, first_name, last_name, email_primary
          ),
          resume:candidate_resumes!parsing_jobs_resume_id_fkey (
            original_filename
          )
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      if (jobId) {
        query = query.eq('job_id', jobId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const candidatesWithJobs = await Promise.all((data || []).map(async (item: any) => {
        if (item.job_id) {
          const { data: jobData } = await supabase
            .from('jobs')
            .select('id, title')
            .eq('id', item.job_id)
            .single();
          return { ...item, job: jobData };
        }
        return item;
      }));

      setParsedCandidates(candidatesWithJobs as ParsedCandidate[]);
    } catch (error) {
      console.error('Error fetching parsed candidates:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParsedCandidates();
    
    const channel = supabase
      .channel('parsing_jobs_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'parsing_jobs' },
        () => fetchParsedCandidates()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [jobId]);

  const toggleSuggestions = (id: string) => {
    setExpandedSuggestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getStatusBadge = (item: ParsedCandidate) => {
    const { status, current_phase, progress_percent } = item;
    
    switch (status) {
      case 'completed':
        return (
          <Badge className="bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20">
            <CheckCircle className="h-3 w-3 mr-1" />
            Completed
          </Badge>
        );
      case 'processing':
        return (
          <div className="flex flex-col gap-1 min-w-[120px]">
            <div className="flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
              <span className="text-xs text-blue-600 dark:text-blue-400">
                {phaseLabels[current_phase] || 'Processing'}
              </span>
            </div>
            <Progress value={progress_percent} className="h-1" />
          </div>
        );
      case 'failed':
        return (
          <Badge className="bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20">
            <AlertCircle className="h-3 w-3 mr-1" />
            Failed
          </Badge>
        );
      default:
        return (
          <Badge className="bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
    }
  };

  const getExtractionStats = (stats: ExtractionStats | null) => {
    if (!stats) return null;
    
    return (
      <TooltipProvider>
        <div className="flex gap-2">
          {stats.experience_count !== undefined && stats.experience_count > 0 && (
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="outline" className="text-xs gap-1">
                  <Briefcase className="h-3 w-3" />
                  {stats.experience_count}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>{stats.experience_count} work experiences</TooltipContent>
            </Tooltip>
          )}
          {stats.education_count !== undefined && stats.education_count > 0 && (
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="outline" className="text-xs gap-1">
                  <GraduationCap className="h-3 w-3" />
                  {stats.education_count}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>{stats.education_count} education records</TooltipContent>
            </Tooltip>
          )}
          {stats.skills_count !== undefined && stats.skills_count > 0 && (
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="outline" className="text-xs gap-1">
                  <Code className="h-3 w-3" />
                  {stats.skills_count}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>{stats.skills_count} skills identified</TooltipContent>
            </Tooltip>
          )}
          {stats.projects_count !== undefined && stats.projects_count > 0 && (
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="outline" className="text-xs gap-1">
                  <FolderKanban className="h-3 w-3" />
                  {stats.projects_count}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>{stats.projects_count} projects found</TooltipContent>
            </Tooltip>
          )}
          {stats.text_improved && (
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="outline" className="text-xs gap-1 border-green-500/30 text-green-600">
                  <Sparkles className="h-3 w-3" />
                  AI Enhanced
                </Badge>
              </TooltipTrigger>
              <TooltipContent>Text quality improved by AI</TooltipContent>
            </Tooltip>
          )}
        </div>
      </TooltipProvider>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (parsedCandidates.length === 0) {
    return (
      <Card className="p-8 text-center">
        <FileText className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
        <h3 className="text-lg font-semibold mb-2">No parsed candidates yet</h3>
        <p className="text-muted-foreground text-sm">
          {jobId 
            ? 'Upload resumes to this job to see parsed candidates here'
            : 'Recently parsed candidates will appear here'
          }
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          {jobId ? 'Parsed Candidates' : 'Recently Parsed Candidates'}
        </h3>
        <Button variant="outline" size="sm" onClick={fetchParsedCandidates}>
          <RefreshCw className="h-4 w-4 mr-1" />
          Refresh
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Candidate</TableHead>
              <TableHead>Resume</TableHead>
              {!jobId && <TableHead>Job</TableHead>}
              <TableHead>Status</TableHead>
              <TableHead>Extracted Data</TableHead>
              <TableHead>Parsed</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {parsedCandidates.map(item => (
              <>
                <TableRow key={item.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <div>
                        {item.candidate?.first_name || 'Unknown'} {item.candidate?.last_name || ''}
                        {item.candidate?.email_primary && (
                          <div className="text-xs text-muted-foreground">{item.candidate.email_primary}</div>
                        )}
                      </div>
                      {item.duplicate_candidate_id && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Badge variant="outline" className="text-xs gap-1 border-orange-500/30 text-orange-600">
                                <Users className="h-3 w-3" />
                                Duplicate?
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>Potential duplicate candidate detected</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground truncate max-w-[200px] block">
                      {item.resume?.original_filename || 'N/A'}
                    </span>
                  </TableCell>
                  {!jobId && (
                    <TableCell>
                      {item.job ? (
                        <span className="text-sm">{item.job.title}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    {getStatusBadge(item)}
                    {item.error_message && (
                      <div className="text-xs text-destructive mt-1 truncate max-w-[150px]" title={item.error_message}>
                        {item.error_message}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {getExtractionStats(item.extraction_stats)}
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {item.ai_suggestions && item.ai_suggestions.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleSuggestions(item.id)}
                          className="text-yellow-600"
                        >
                          <Lightbulb className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedCandidateId(item.candidate_id)}
                        disabled={item.status !== 'completed'}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                {/* Suggestions row */}
                {expandedSuggestions.has(item.id) && item.ai_suggestions && item.ai_suggestions.length > 0 && (
                  <TableRow key={`${item.id}-suggestions`}>
                    <TableCell colSpan={jobId ? 6 : 7} className="bg-yellow-50/50 dark:bg-yellow-950/20">
                      <div className="flex items-start gap-2 p-2">
                        <Brain className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-yellow-700 dark:text-yellow-400 mb-1">
                            AI Suggestions
                          </p>
                          <ul className="text-sm text-muted-foreground space-y-1">
                            {item.ai_suggestions.map((suggestion, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="text-yellow-500">•</span>
                                {suggestion}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            ))}
          </TableBody>
        </Table>
      </Card>

      <CandidateDetailDialog
        candidateId={selectedCandidateId || ''}
        open={!!selectedCandidateId}
        onOpenChange={(open) => !open && setSelectedCandidateId(null)}
      />
    </div>
  );
};

export default ParsedCandidatesView;
